import { useInfiniteQuery } from '@tanstack/react-query';
import type { AuditEntryView } from '@lp/contracts';
import { Fragment, useState } from 'react';
import { errorText, get } from '@/lib/api';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Button, Empty, ErrorBox, Loading, PageHeader } from '@/components/ui';
import { useClients } from './clients';

const PAGE = 50;

/** every change and sign-in, newest first — who, what, when, on what, before and after */
export function AuditPage() {
  const t = useT();
  const clients = useClients();
  const nameOf = new Map((clients.data?.items ?? []).map((c) => [c.id, c.code]));
  const [open, setOpen] = useState<number | null>(null);
  const q = useInfiniteQuery({
    queryKey: ['audit'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => get<AuditEntryView[]>(`/platform/audit?limit=${PAGE}${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => (last.length === PAGE ? last[last.length - 1]!.id : undefined),
  });
  const rows = q.data?.pages.flat() ?? [];

  return (
    <>
      <PageHeader title={t('Audit')} subtitle={t('Every change and every sign-in, newest first. Nothing here can be changed or removed.')} />
      <div className="rounded-lg border border-ink-200 bg-white">
        {q.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : q.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(q.error)} />
          </div>
        ) : !rows.length ? (
          <Empty title={t('Nothing yet.')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="px-4 py-2">{t('When')}</th>
                  <th className="px-4 py-2">{t('What')}</th>
                  <th className="px-4 py-2">{t('Who')}</th>
                  <th className="px-4 py-2">{t('Client')}</th>
                  <th className="px-4 py-2">{t('On')}</th>
                  <th className="px-4 py-2">{t('Address')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <Fragment key={e.id}>
                    <tr onClick={() => setOpen(open === e.id ? null : e.id)} className="cursor-pointer border-b border-ink-100 hover:bg-ink-50">
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-ink-600">{dateTime(e.at)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                      <td className="px-4 py-2 text-xs">{e.actor.username ?? t('System')}</td>
                      <td className="px-4 py-2 font-mono text-xs">{e.clientId ? (nameOf.get(e.clientId) ?? `#${e.clientId}`) : '—'}</td>
                      <td className="px-4 py-2 text-xs text-ink-600">
                        {e.target.type}
                        {e.target.id ? ` ${e.target.id}` : ''}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-ink-500">{e.ip ?? '—'}</td>
                    </tr>
                    {open === e.id && (
                      <tr className="border-b border-ink-100 bg-ink-50">
                        <td colSpan={6} className="px-4 py-2">
                          <div className="grid gap-2 text-xs sm:grid-cols-2">
                            <pre className="overflow-x-auto rounded bg-white p-2">{t('Before')}: {JSON.stringify(e.before, null, 2) ?? '—'}</pre>
                            <pre className="overflow-x-auto rounded bg-white p-2">{t('After')}: {JSON.stringify(e.after, null, 2) ?? '—'}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {q.hasNextPage && (
        <div className="mt-3 text-center">
          <Button variant="secondary" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {t('Show older')}
          </Button>
        </div>
      )}
    </>
  );
}
